import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row, Button } from "react-bootstrap";
import { Users, Settings, Plus, UserPlus } from "react-feather";
import { ModalContext } from "context/ModalContext";
import CreateUserModal from 'components/widgets/modals/CreateUserModal';
import TableComponent from 'components/widgets/table/index'; 

const UserManagement = () => {
  const history = useHistory();
  const { setModalShow, modalShow, modalClose } = useContext(ModalContext);

  // Mock roles data
  const [roles, setRoles] = useState([
    {
      id: 'admin',
      name: 'Admin',
      description: 'Full system access and management',
      status: 'active'
    },
    {
      id: 'salesrep',
      name: 'Sales Representative',
      description: 'Manage sales and customer interactions',
      status: 'active'
    },
    {
      id: 'underwriter',
      name: 'Underwriter',
      description: 'Handle insurance assessments',
      status: 'inactive'
    }
  ]);

  // Mock users data
  const [users, setUsers] = useState([
    {
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john@example.com',
      username: 'johndoe',
      status: 'active',
      role: 'admin'
    },
    {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane@example.com',
      username: 'janesmith',
      status: 'inactive',
      role: 'salesrep'
    },
    {
      id: 3,
      firstName: 'Mike',
      lastName: 'Johnson',
      email: 'mike@example.com',
      username: 'mikej',
      status: 'active',
      role: 'underwriter'
    }
  ]);

  // Transform users data to include fullName
  const transformedUsers = users.map(user => ({
    ...user,
    fullName: `${user.firstName} ${user.lastName}`
  }));

  // User stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;
  const inactiveUsers = users.filter(u => u.status === 'inactive').length;

  // Define table fields and row data for roles
  const roleTableFields = [
    { name: 'Name' },
    { name: 'Description' },
    { name: 'Status' },
    { name: 'Action' }
  ];

  const roleRowData = [
    { type: 'text', field: 'name' },
    { type: 'text', field: 'description' },
    { 
      type: 'text', 
      field: 'status',
      transform: (data) => (
        <span
          style={{
            color: data.status === 'active' ? '#22c55e' : '#ef4444',
            fontWeight: '500'
          }}
        >
          {data.status ? data.status.toUpperCase() : 'N/A'}
        </span>
      )
    },
    {
      type: 'action',
      field: 'id',
      actions: [
        {
          method: (id) => history.push(`/dashboard/modules/usermanagement/viewrole/${id}`),
          field: 'id',
          title: 'View'
        }
      ]
    }
  ];

  // Define table fields and row data for users
  const userTableFields = [
    { name: 'Name' },
    { name: 'Email' },
    { name: 'Username' },
    { name: 'Role' },
    { name: 'Status' },
    { name: 'Action' }
  ];

  const userRowData = [
    { 
      type: 'text', 
      field: 'fullName',
      transform: (data) => `${data.firstName} ${data.lastName}`
    },
    { type: 'text', field: 'email' },
    { type: 'text', field: 'username' },
    { type: 'text', field: 'role' },
    { 
      type: 'text', 
      field: 'status',
      transform: (data) => (
        <span
          style={{
            color: data.status === 'active' ? '#22c55e' : '#ef4444',
            fontWeight: '500'
          }}
        >
          {data.status ? data.status.toUpperCase() : 'N/A'}
        </span>
      )
    },
    {
      type: 'action',
      field: 'id',
      actions: [
        {
          method: (id) => history.push(`/dashboard/modules/usermanagement/viewuser/${id}`),
          field: 'id',
          title: 'View'
        }
      ]
    }
  ];

  const handleCreateUser = () => {
    setModalShow(true);
  };

  const handleUserCreated = (newUser) => {
    setUsers(prev => [...prev, newUser]);
    modalClose();
  };

  return (
    <div className="card p-4" style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header Section */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <Users size={32} className="text-primary mr-3" />
            <h2 className="mb-0" style={{ fontWeight: '600', color: '#1f2937' }}>
              User Management Module
            </h2>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => history.push('/dashboard/modules/usermanagement/configure')}
            style={{ 
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Settings size={14} className="mr-2" />
            Configure Module
          </Button>
        </div>
        <p style={{ fontSize: '1rem', color: '#6b7280', margin: 0 }}>
          Manage users, roles, and permissions in the system
        </p>
      </div>

      {/* Create User Modal */}
      <CreateUserModal 
        show={modalShow} 
        onHide={modalClose} 
        onUserCreated={handleUserCreated}
      />

      {/* Roles Management Section */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 style={{ fontWeight: '600', color: '#1f2937' }}>Role Management</h4>
          <Button
            variant="primary"
            size="sm"
            onClick={() => history.push('/dashboard/modules/usermanagement/roles/create')}
            style={{ fontSize: '0.85rem' }}
          >
            <Plus size={14} className="mr-1" />
            Create Role
          </Button>
        </div>
        
        <TableComponent
          table_fields={roleTableFields}
          table_data={roles} // Use roles directly, no need for transformedRoles
          row_data={roleRowData}
          title="Roles"
          pageSize={5}
        />
      </div>

      {/* Users Section */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 style={{ fontWeight: '600', color: '#1f2937' }}>Users</h4>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreateUser}
            style={{ fontSize: '0.85rem' }}
          >
            <UserPlus size={14} className="mr-1" />
            Create User
          </Button>
        </div>

        {/* User Stats Cards */}
        <Row className="mb-4">
          <Col md={4}>
            <Card 
              className="shadow-sm text-center p-3" 
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
            >
              <h6 style={{ color: '#6b7280', fontSize: '0.9rem' }}>Total Users</h6>
              <h3 style={{ color: '#1f2937', fontWeight: '600' }}>{totalUsers}</h3>
            </Card>
          </Col>
          <Col md={4}>
            <Card 
              className="shadow-sm text-center p-3" 
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
            >
              <h6 style={{ color: '#6b7280', fontSize: '0.9rem' }}>Active</h6>
              <h3 style={{ color: '#22c55e', fontWeight: '600' }}>{activeUsers}</h3>
            </Card>
          </Col>
          <Col md={4}>
            <Card 
              className="shadow-sm text-center p-3" 
              style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}
            >
              <h6 style={{ color: '#6b7280', fontSize: '0.9rem' }}>Inactive</h6>
              <h3 style={{ color: '#ef4444', fontWeight: '600' }}>{inactiveUsers}</h3>
            </Card>
          </Col>
        </Row>

        {/* Users Table */}
        <TableComponent
          table_fields={userTableFields}
          table_data={transformedUsers}
          row_data={userRowData}
          title="Users"
          pageSize={5}
        />
      </div>
    </div>
  );
};

export default UserManagement;